/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { ReactNode } from 'react';
import type { LabelledSwitchProps } from './types';
import { Box, Typography, useTheme } from '@mui/material';

export function LabelledSwitch( { active, onToggle, width, disabled, id, text, textActive }: LabelledSwitchProps ): ReactNode {
	const { palette } = useTheme();

	const textBoxStyle = {
		color: 'white',
		overflow: 'hidden',
		textAlign: 'center',
		transition: 'all 0.5s',
		fontWeight: '400',
		fontSize: '12px',
		lineHeight: '20px',
		whiteSpace: 'nowrap'
	};

	return (
		<Box
			id={ id }
			role={ 'checkbox' }
			aria-checked={ active }
			onClick={ event => {
				event.preventDefault();
				event.stopPropagation();

				if ( !disabled ) {
					onToggle();
				}
			} }
			sx={ {
				padding: '0 4px',
				display: 'flex',
				alignItems: 'center',
				cursor: disabled ? 'inherit' : 'pointer',
				userSelect: 'none',
				borderRadius: '99px',
				height: '22px',
				transition: 'all 0.5s',
				backgroundColor: active ? palette.primary.main : '#a3b1c3',
				opacity: disabled ? 0.5 : 1
			} }
		>
			<Typography sx={ { ...textBoxStyle, width: active ? width : 0 } }>
				{ textActive || text }
			</Typography>
			<Bubble/>
			<Typography sx={ { ...textBoxStyle, width: active ? 0 : width	} }>
				{ text }
			</Typography>
		</Box>
	);
}

function Bubble(): ReactNode {
	return (
		<Box
			sx={ {
				width: '12px',
				height: '12px',
				borderRadius: '12px',
				backgroundColor: 'white',
				boxShadow: '0 0 3px black'
			} }
		/>
	);
}
